/* eslint-disable react/require-default-props,react/destructuring-assignment */
import React, { Component, Fragment } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { ThemeProvider } from 'react-css-themr';
import DatePicker from 'react-toolbox/lib/date_picker';
import format from 'date-fns-old/format';
import formatStringByPattern from 'format-string-by-pattern';
import TextInput from '../../TextInput';
import { DATE_PATTERN_UI } from '../../../constants/strings';
import { convertDateToAPIFormat } from '../../../utils/converters/dateConverters';
import { isIE } from '../../../utils/browserChecker';
import theme from './theme';
import Icon from '../../Icon';

const TEXT_INPUT_CASE = isIE();
const MASK_PATTERN = '99-99-9999';

@injectIntl
export default class DatePickerCustom extends Component {
  static propTypes = {
    withManualInputDob: PropTypes.bool,
    formatDate: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.instanceOf(Date),
    ]),
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func,
    }),
    iconClassName: PropTypes.string,
  };

  constructor(props) {
    super(props);
    const { value } = props;

    this.state = {
      date: this.convertToDate(value),
      isActiveCalendar: false,
      manualDate: this.convertToManualDate(value),
      withManualInputDobPickerOpen: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    if (value !== prevProps.value) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        date: value,
      });
    }
  }

  convertToManualDate = value => {
    if (!value) return null;

    return format(
      value,
      this.props.formatDate || this.props.intl.formatMessage(DATE_PATTERN_UI),
    );
  };

  applyMask = value => {
    return formatStringByPattern(MASK_PATTERN, value);
  };

  triggerOnChange = maskedValue => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(maskedValue);
    }
  };

  handleChange = value => {
    this.setState({
      date: this.convertToDate(value),
      manualDate: this.convertToManualDate(value),
    });
    this.toggleCalendar();

    this.triggerOnChange(value);

    // Safari 10 placeholder bug fix
    const input = document.querySelector('.datePicker input');
    if (input) {
      setTimeout(() => {
        input.value = input.value;
      }, 0);
    }
  };

  handleFocus = () => {
    this.setState({ visibility: 'visible' });
  };

  convertToDate = value => {
    const {
      intl: { formatMessage },
    } = this.props;

    if (TEXT_INPUT_CASE) return value;

    return typeof value === 'string'
      ? new Date(convertDateToAPIFormat(value, formatMessage))
      : value;
  };

  handleIconClick = () => {
    this.setState({
      isActiveCalendar: true,
      withManualInputDobPickerOpen: true,
    });
  };

  toggleCalendar = () => {
    this.setState({
      isActiveCalendar: false,
      withManualInputDobPickerOpen: false,
    });
  };

  handleManualDateChange = value => {
    const {
      intl: { formatMessage },
      formatDate,
    } = this.props;

    const stateData = format(
      this.state.date,
      formatDate || formatMessage(DATE_PATTERN_UI),
    );

    const onlyNumbers = value.replace(/[^\d-]/g, '');

    const maskedValue = this.applyMask(onlyNumbers);

    if (onlyNumbers.length < stateData.length) {
      this.setState({
        manualDate: maskedValue,
      });

      this.triggerOnChange(maskedValue);

      return;
    }

    if (onlyNumbers.length === 10) {
      this.setState({
        date: this.convertToDate(maskedValue),
      });

      this.triggerOnChange(maskedValue);
    }
  };

  render() {
    const {
      className,
      disabled,
      id,
      label,
      name,
      maxDate,
      minDate,
      style,
      onBlur,
      intl: { formatMessage },
      formatDate,
      withManualInputDob,
      iconClassName,
    } = this.props;

    if (TEXT_INPUT_CASE) {
      return (
        <TextInput
          {...{ className, disabled, id, label, name, onBlur }}
          placeholder={formatMessage(DATE_PATTERN_UI)}
          onChange={this.handleChange}
          value={this.state.date}
          type="text"
          rounded
        />
      );
    }

    if (withManualInputDob) {
      const dateFieldValue =
        this.state.manualDate ||
        (this.state.date && this.convertToManualDate(this.state.date));

      return (
        <ThemeProvider {...{ theme }}>
          <Fragment>
            <div className={theme.RTDatePicker.mainDobInput}>
              <TextInput
                disabled={disabled}
                value={dateFieldValue}
                className={theme.RTDatePicker.mainDobInput}
                placeholder={label}
                label={label}
                type="text"
                onFocus={this.handleFocus}
                onChange={this.handleManualDateChange}
                limit={10}
              />
              <Icon
                size={18}
                name="icon-date"
                disabled={disabled}
                onClick={this.handleIconClick}
                className={cn(theme.RTDatePicker.calendarIcon, iconClassName, {
                  [theme.RTDatePicker.disabledIcon]: disabled,
                })}
              />
            </div>

            {this.state.withManualInputDobPickerOpen && (
              <DatePicker
                {...{ id, name, maxDate, minDate }}
                sundayFirstDayOfWeek
                inputClassName={`${className} datePicker ${theme.RTDatePicker.absoluteInput}`}
                inputFormat={value =>
                  format(value, formatDate || formatMessage(DATE_PATTERN_UI))
                }
                onChange={this.handleChange}
                value={this.state.date}
                readonly={disabled}
                placeholder={label}
                active={this.state.isActiveCalendar}
                onDismiss={this.toggleCalendar}
                style={{ visibility: disabled ? 'hidden' : 'visible' }}
              />
            )}
          </Fragment>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider {...{ theme }}>
        <DatePicker
          {...{ id, label, name, maxDate, minDate, style }}
          sundayFirstDayOfWeek
          inputClassName={`${className} datePicker`}
          inputFormat={value =>
            format(value, formatDate || formatMessage(DATE_PATTERN_UI))
          }
          onChange={this.handleChange}
          value={this.state.date}
          readonly={disabled}
          placeholder={label}
        />
      </ThemeProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/common/DatePickerCustom/index.jsx